// src/pages/RoleManagementPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "@/api/apiCall";
import * as yup from "yup";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Shield, Loader2, SquarePen } from "lucide-react";
import DataTable from "@/components/common/DataTable";
import SeeMore from "@/components/common/SeeMore";
import useDebounce from "@/hooks/useDebounce";
import { formatDateTime } from "@/lib/formatDateTime";
import { usePermissions } from "@/context/PermissionContext";

const roleSchema = yup.object().shape({
  role_name: yup.string().required("Role name is required").min(3).max(50),
  description: yup.string().max(500).nullable(),
});

const RoleManagementPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // ── State ──────────────────────────────────────────────────────────────────
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, perPage: 10, totalRecords: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ role_name: "", description: "" });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const hasMounted = useRef(false);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchRoles = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);
      let url = `/get-all-roles?page=${page}&perPage=${perPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (debouncedSearch.trim()) url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
      const response = await apiCall("GET", url, null, navigate);
      if (response?.success) {
        setRoles(response.data.roles || []);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      console.error("Failed to load roles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    fetchRoles(1);
  }, []);

  useEffect(() => {
    if (!hasMounted.current) return;
     fetchRoles(1);
  }, [debouncedSearch, sortBy, sortOrder]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortBy === col) setSortOrder((p) => (p === "ASC" ? "DESC" : "ASC"));
    else { setSortBy(col); setSortOrder("ASC"); }
  };

  const generateRoleKey = (name) =>
    name.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "").replace(/_+/g, "_").replace(/^_+|_+$/g, "");

  const resetForm = () => { setFormData({ role_name: "", description: "" }); setEditMode(false); setSelectedRoleId(null); setErrors({}); };

  const handleOpenDialog = (role = null) => {
    if (role) { setEditMode(true); setSelectedRoleId(role.role_id); setFormData({ role_name: role.role_name, description: role.description || "" }); }
    else resetForm();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setTimeout(resetForm, 200); };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    try {
      await roleSchema.validate(formData, { abortEarly: false });
      setSubmitting(true);
      const payload = { role_name: formData.role_name.trim(), role_key: generateRoleKey(formData.role_name), description: formData.description.trim() || null };
      if (editMode) await apiCall("POST", `/update-role/${selectedRoleId}`, payload, navigate);
      else await apiCall("POST", "/create-role", payload, navigate);
      handleCloseDialog();
      fetchRoles(pagination.currentPage, pagination.perPage);
    } catch (err) {
      if (err.name === "ValidationError") {
        const e = {}; err.inner.forEach((er) => { e[er.path] = er.message; }); setErrors(e);
      }
    } finally { setSubmitting(false); }
  };

  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      await apiCall("POST", `/delete-role/${selectedRoleId}`, null, navigate);
      setDeleteDialogOpen(false);
      fetchRoles(pagination.currentPage, pagination.perPage);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); setSelectedRoleId(null); }
  };

  // ── DataTable config ───────────────────────────────────────────────────────
  const columns = [
    {
      key: "role_name",
      label: "Role Name",
      sortable: true,
      render: (row) => <div className="font-medium">{row.role_name}</div>,
    },
    {
      key: "role_key",
      label: "Role Key",
      sortable: true,
      render: (row) => <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.role_key}</code>,
    },
    {
      key: "description",
      label: "Description",
      render: (row) => row.description ? <SeeMore text={row.description} maxLength={20} /> : "—",
    },
    {
      key: "created_by_name",
      label: "Created By",
      sortable: true,
    },
    {
      key: "created_datetime",
      label: "Created At",
      sortable: true,
      render: (row) => <span className="text-muted-foreground text-sm">{formatDateTime(row.created_datetime)}</span>,
    },
  ];

  const rowActions = (row) => [
    // {
    //   label: "Edit",
    //   icon: <SquarePen className="h-4 w-4" />,
    //   onClick: () => handleOpenDialog(row),
    //   hidden: !hasPermission("roles_edit"),
    // },
    {
        label: "Edit",
        icon: (
          <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#DBFCE780] text-[#407E3B] dark:bg-[#DBFCE715] dark:text-[#6DBF67]">
            <SquarePen className="h-4 w-4" />
          </span>
        ),
        onClick: () => handleOpenDialog(row),
        hidden: !hasPermission("roles_edit"),
      },
    // {
    //   label: "Delete",
    //   icon: <Trash2 className="h-4 w-4" />,
    //   onClick: () => { setSelectedRoleId(row.role_id); setDeleteDialogOpen(true); },
    //   className: "text-destructive focus:text-destructive",
    //   hidden: !hasPermission("roles_delete"),
    // },
    {
        label: "Delete",
        icon: (
          <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#FAE6EC80] text-[#D3663D] dark:bg-[#FAE6EC15] dark:text-[#F28B6E]">
            <Trash2 className="h-4 w-4" />
          </span>
        ),
         onClick: () => { setSelectedRoleId(row.role_id); setDeleteDialogOpen(true); },
        className: "text-destructive focus:text-destructive",
        hidden: !hasPermission("roles_delete"),
      },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <DataTable
        title="Role Management"
        description="Create, update and manage system roles"
        // icon={<Shield className="h-5 w-5" />}
        headerActions={
          hasPermission("roles_create") && (
            <Button onClick={() => handleOpenDialog()} className="gap-2 w-full sm:w-auto h-9 text-sm">
              <Plus className="h-4 w-4" /> Add Role
            </Button>
          )
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search roles..."
        columns={columns}
        rows={roles}
        rowKey="role_id"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        pagination={pagination}
        onPageChange={(p) => fetchRoles(p, pagination.perPage)}
        onPerPageChange={(pp) => fetchRoles(1, Number(pp))}
        paginationTitle="roles"
        loading={loading}
        emptyMessage="No roles found. Create your first role to get started."
        rowActions={rowActions}
      />

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Role" : "Create New Role"}</DialogTitle>
            <DialogDescription>
              {editMode ? "Update role details below." : "Fill in the details to create a new role."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-1.5">
                <Label htmlFor="role_name">Role Name <span className="text-destructive">*</span></Label>
                <Input id="role_name" name="role_name" value={formData.role_name} onChange={handleInputChange} placeholder="e.g. Admin" className={errors.role_name ? "border-destructive" : ""}  autoComplete="off" />
                {errors.role_name && <p className="text-xs text-destructive">{errors.role_name}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Optional role description..." rows={3} className={errors.description ? "border-destructive" : ""}  autoComplete="off" />
                {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
              </div>
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{editMode ? "Updating..." : "Creating..."}</> : editMode ? "Update Role" : "Create Role"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ── */}
      {/* ── Delete Dialog ── */}
<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
  <DialogContent className="w-[95vw] max-w-md p-4">
    <DialogHeader className="pb-1">
      <DialogTitle className="text-destructive text-base">Delete Role</DialogTitle>
      <DialogDescription className="text-sm">
        Are you sure you want to delete this role? This action cannot be undone.
      </DialogDescription>
    </DialogHeader>
    
    {/* Role Details */}
    <div className="py-2 px-3 border rounded-lg bg-muted/30">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Role Name</span>
          <span className="text-sm font-medium">
            {roles.find(r => r.role_id === selectedRoleId)?.role_name || 'Unknown Role'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Role Key</span>
          <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
            {roles.find(r => r.role_id === selectedRoleId)?.role_key || 'N/A'}
          </span>
        </div>
        
        <div className="flex items-center justify-between pt-1 border-t">
          <span className="text-xs text-muted-foreground">Created By</span>
          <span className="text-sm">
            {roles.find(r => r.role_id === selectedRoleId)?.created_by_name || '—'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Created At</span>
          <span className="text-sm">
            {roles.find(r => r.role_id === selectedRoleId)?.created_datetime 
              ? formatDateTime(roles.find(r => r.role_id === selectedRoleId).created_datetime) 
              : '—'}
          </span>
        </div>
      </div>
    </div>
    
    <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
      <Button 
        type="button" 
        variant="outline" 
        onClick={() => setDeleteDialogOpen(false)} 
        className="w-full sm:w-auto h-8 text-sm"
      >
        Cancel
      </Button>
      <Button 
        type="button" 
        onClick={handleDeleteConfirm} 
        disabled={submitting} 
        className="w-full sm:w-auto bg-destructive text-white hover:bg-destructive/90 h-8 text-sm"
      >
        {submitting ? (
          <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Deleting...</>
        ) : (
          "Delete Role"
        )}
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
    </>
  );
};

export default RoleManagementPage;